import { BrandMark } from "./BrandMark";
import { ThemeToggle } from "./ThemeToggle";

function Bone({ className = "" }: { className?: string }) {
  return <span aria-hidden="true" className={`skeleton-bone ${className}`} />;
}

export function QueueSkeleton() {
  return (
    <main aria-busy="true" aria-label="Loading review queue" className="queue-shell queue-loading-skeleton">
      <header className="queue-header">
        <span className="brand"><BrandMark /><strong>CHEW</strong></span>
        <div className="skeleton-queue-lenses"><Bone /><Bone /></div>
        <div className="queue-header-tools"><Bone className="skeleton-icon" /><ThemeToggle /></div>
      </header>
      <section className="next-review">
        <div className="next-kicker"><Bone className="skeleton-kicker" /></div>
        <article className="next-card skeleton-next-card">
          <header><Bone className="skeleton-repository" /><div className="skeleton-pills"><Bone /><Bone /><Bone /></div></header>
          <Bone className="skeleton-title skeleton-title-long" />
          <Bone className="skeleton-title skeleton-title-short" />
          <div className="skeleton-copy"><Bone /><Bone /></div>
          <div className="skeleton-meta"><Bone /><Bone /><Bone /></div>
          <footer><Bone className="skeleton-secondary-action" /><Bone className="skeleton-primary-action" /></footer>
        </article>
      </section>
      <span className="sr-only">Finding your next review.</span>
    </main>
  );
}

/**
 * Skeleton for pages that render inside the AppShell header. Using the same
 * `.app-header` structure keeps the brand, nav, and tools from jumping when
 * the real page replaces the skeleton (the queue skeleton's 46px header
 * caused a visible 2px shift plus a full reshuffle on every load).
 */
export function AppPageSkeleton({ label = "Loading" }: { label?: string }) {
  return (
    <div aria-busy="true" aria-label={label} className="app-frame app-page-skeleton">
      <header className="app-header">
        <div className="app-header-start">
          <span className="brand"><BrandMark /><strong>CHEW</strong></span>
          <div className="skeleton-app-nav"><Bone /><Bone /></div>
        </div>
        <div className="app-header-center"><Bone className="skeleton-kicker" /></div>
        <div className="app-header-tools"><Bone className="skeleton-icon" /><ThemeToggle /></div>
      </header>
      <div className="app-skeleton-body">
        <Bone className="skeleton-heading" />
        <div className="skeleton-copy"><Bone /><Bone /><Bone /></div>
      </div>
      <span className="sr-only">{label}.</span>
    </div>
  );
}

function SkeletonPane({ kind }: { kind: "guide" | "files" }) {
  return (
    <aside className={`skeleton-review-pane skeleton-${kind}`}>
      <div className="skeleton-pane-tabs"><Bone /><Bone /></div>
      <div className="skeleton-pane-body">
        <Bone className="skeleton-eyebrow" />
        <Bone className="skeleton-heading" />
        <Bone className="skeleton-heading skeleton-heading-short" />
        <div className="skeleton-pane-lines"><Bone /><Bone /><Bone /><Bone /><Bone /></div>
      </div>
    </aside>
  );
}

function SkeletonEditor() {
  return (
    <section className="skeleton-review-editor">
      <div className="skeleton-editor-context"><Bone /><Bone /></div>
      <div className="skeleton-file-header"><Bone className="skeleton-file-icon" /><Bone /><Bone className="skeleton-file-stat" /></div>
      <div className="skeleton-code-lines">
        {Array.from({ length: 17 }, (_, index) => (
          <div className={index === 5 || index === 6 || index === 11 ? "changed" : ""} key={index}>
            <Bone className="skeleton-line-number" /><Bone className={`skeleton-code skeleton-code-${index % 4}`} />
          </div>
        ))}
      </div>
      <div className="skeleton-composer"><Bone /><Bone className="skeleton-composer-action" /></div>
    </section>
  );
}

export function ReviewSkeleton({
  pullNumber,
  repository,
}: {
  pullNumber?: number;
  repository?: string;
}) {
  return (
    <div aria-busy="true" aria-label="Loading pull request" className="review-shell review-loading-skeleton">
      <header className="app-header">
        <span className="brand"><BrandMark /><strong>CHEW</strong></span>
        <div className="pr-identity">
          <span className="pr-repository">{repository ?? "Loading repository"}</span>
          {pullNumber ? <strong>#{pullNumber}</strong> : <Bone className="skeleton-pr-number" />}
        </div>
        <div className="header-actions"><Bone className="skeleton-header-action" /><Bone className="skeleton-header-action" /><ThemeToggle /></div>
      </header>
      <div className="walkthrough-progress skeleton-progress-header">
        <Bone className="skeleton-progress-label" />
        <div className="skeleton-progress-track"><Bone /><Bone /><Bone /><Bone /><Bone /></div>
        <Bone className="skeleton-progress-total" />
      </div>
      <main className="guided-main workspace-main">
        {/* Deliberately NOT .review-workspace: the skeleton must never read as
            the real workspace to styles or tests probing for it. */}
        <article className="skeleton-review-workspace">
          <SkeletonPane kind="guide" />
          <SkeletonEditor />
          <SkeletonPane kind="files" />
        </article>
      </main>
      <span className="sr-only">Preparing the review workspace.</span>
    </div>
  );
}

export function FileDiffSkeleton({ path }: { path: string }) {
  return (
    <div aria-label={`Loading ${path}`} className="file-diff-skeleton" role="status">
      {Array.from({ length: 9 }, (_, index) => (
        <div className={index === 3 || index === 4 ? "changed" : ""} key={index}>
          <Bone className="skeleton-line-number" />
          <Bone className={`skeleton-code skeleton-code-${index % 4}`} />
        </div>
      ))}
      <span className="sr-only">Fetching this file from GitHub.</span>
    </div>
  );
}
